import React, { memo, useRef } from 'react';
import { css, Theme, useTheme } from "@emotion/react";

import useElementDimensions from "core/hooks/useElementDimensions";
import { getPrice } from "core/includes/formatters";

type Props = {
    item: CartProduct,
    hasPromoPrice?: boolean,
    isLast?: boolean,
}

const SummaryItemRow: React.FC<Props> = (
    {
        item,
        hasPromoPrice = false,
        isLast = false,
    }
) => {
    const theme: Theme = useTheme();
    const itemHasDiscount = item.discountedPrice > 0;
    const totalAmount = item.unitPrice * item.quantity;
    const totalPrice = (Math.round(totalAmount * 100) / 100).toFixed(2);
    const priceRef = useRef<HTMLDivElement>(null);
    const priceWidth = useElementDimensions(priceRef.current).width;

    const styles = css`
        display: flex;
        width: 100%;
        font-size: 18px;
        line-height: 25px;
        justify-content: space-between;

        ${theme.breakpoints.down('lg')} {
            font-size: 16px;
            line-height: 19px;
        }

        ${isLast ? css`
            margin-bottom: 43px;
        ` : css`
            margin-bottom: 30px;
        `}
    `;

    const titleStyle = css`
        width: 212px;
        margin-right: 34px;

        ${itemHasDiscount && css`
            margin-top: 20px;
        `}
        ${theme.breakpoints.only('lg')} {
            width: 183px;
        }

        ${theme.breakpoints.only('md')} {
            width: 287px;
        }

        ${theme.breakpoints.down('sm')} {
            width: 291px;
        }
    `;

    const promoLabelStyle = css`
        font-size: 13px;
        line-height: 16px;
        color: ${theme.colours.gold};

        ${theme.breakpoints.down('lg')} {
            font-size: 10px;
            line-height: 13px;
        }
    `;

    const priceWrapperStyle = css`
        text-align: right;
        ${hasPromoPrice && css`
            width: 115px;

            ${theme.breakpoints.down('sm')} {
                width: fit-content;
                min-width: 115px;
            }
        `}
    `;

    const priceContainerStyle = css`
        position: relative;
        text-align: right;
        margin-right: 6px;
    `;

    const priceLabelStyle = css`
        width: fit-content;
        margin-left: auto;
    `;

    const lineThroughStyle = css`
        position: absolute;
        top: 50%;
        right: -5%;
        height: 3px;
        // Needs to be longer than the price itself
        width: ${priceWidth + 10}px;
        transform: translateY(-50%);
        background: ${theme.colours.gold};
        border-radius: 3px;

        ${theme.breakpoints.only('md')} {
            right: -3%;
        }
    `;

    const promoPriceStyle = css`
        text-align: right;
        color: ${theme.colours.gold};

        ${theme.breakpoints.up('xl')} {
            font-size: 18px;
            line-height: 25px;
        }
    `;

    const quantityStyle = css`
        white-space: nowrap;
        ${itemHasDiscount && css`
            margin-top: 20px;
        `};
    `;

    return (
        <div css={styles}>
            <div css={titleStyle}>{item.title}</div>
            <div css={quantityStyle}>x {item.quantity}</div>
            <div css={priceWrapperStyle}>
                {itemHasDiscount && (
                    <span css={promoLabelStyle}>With promo code</span>
                )}
                <div css={priceContainerStyle}>
                    <div css={priceLabelStyle} ref={priceRef}>
                        ${getPrice(totalPrice)}
                    </div>
                    {itemHasDiscount && (
                        <div css={lineThroughStyle} />
                    )}
                </div>
                {itemHasDiscount && (
                    <div css={promoPriceStyle}>${getPrice(totalAmount - item.discountedPrice)}</div>
                )}
            </div>
        </div>
    );
};

export default memo(SummaryItemRow);